import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../classes/appApi';
import { useOffline } from '../components/App/AppOfflineContext';
import BusinessFunctionBadge from '../components/BusinessFunctionBadge';
import EqxPdfViewer from '../components/EqxPdfViewer';
import MainLayout, { useMainLayoutContext } from '../components/MainLayout';
import { ControlledDocumentModel } from '../defs/defs';
import EqxCard from '../shared/components/EqxCard/EqxCard';
import { EqxLink } from '@equinoxx/react';
import EqxRequestHandler from '../shared/components/EqxRequestHandler/EqxRequestHandler';
import EqxTile, { EqxTileBlank } from '../shared/components/EqxTile/EqxTile';
import EqxTileGroup from '../shared/components/EqxTile/EqxTileGroup';
import useApiEndpoint from '../shared/hooks/ApiEndpoint';
import { formatDate } from '../shared/utils/formatDate';
import { sanitizeHtml } from '../shared/utils/sanitizeHtml';

const PdfViewerPanel = (props: { document: ControlledDocumentModel }) => {
    const { document } = props
    const [content, setContent] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState<boolean>(true)

    const api = useApi()

    const showPanel = document.url != null
    if (!showPanel) return <></>;

    const endpoint = api.endpoints.controlledDocumentContent(document.id.toString()).url;
    api.fetchBinary(endpoint).then(content => {
        setContent(content);
    }).catch(error => {
        setError(error);
    }).finally(() => {
        setLoading(false);
    })

    return <EqxRequestHandler state={{ loading, error: error ?? null }} content={() => {
        return <EqxPdfViewer content={content!} />
    }} />
}

const ControlledDocumentActions = (props: {
    document: ControlledDocumentModel;
    documentId: string;
    onDocumentUpdated: () => void;
}) => {
    const { document, documentId, onDocumentUpdated } = props;
    const api = useApi();
    const mainLayoutContext = useMainLayoutContext();
    const { isOffline } = useOffline();
    const {
        addSuccessMessage: showGlobalSuccess,
        addErrorMessage: showGlobalError,
        addLoadingMessage: showGlobalLoading,
    } = mainLayoutContext.messages;

    const handleAcceptDocument = () => {
        if (!document || !documentId) return;

        showGlobalLoading("Loading...", "controlled-doc-accept");
        return api.endpoints.acceptControlledDocument(documentId)
            .execute(api)
            .then(() => {
                // Reload the document to get updated acceptance status
                onDocumentUpdated();
                showGlobalSuccess("Document accepted successfully", "controlled-doc-accept");
            })
            .catch((error) => {
                showGlobalError("An error occurred while accepting the document", "controlled-doc-accept");
            });
    };

    return (
        <EqxTileGroup size="xs">
            <EqxTile
                className='col-6'
                title={document.url ? "Download" : "File not available"}
                icon="fas fa-download"
                color="primary"
                disabled={!document.url}
                link={document.url || "#"}
                target={document.url ? "_blank" : undefined}
            />
            {document.acceptanceRequired ?
                document.currentUserHasAccepted ?
                    <EqxTile
                        className='col-6'
                        title="Accepted"
                        icon="fas fa-check"
                        color="success"
                        disabled={true}
                    /> :
                    <EqxTile
                        className='col-6'
                        title="Accept"
                        icon="fas fa-check"
                        color="success"
                        onClick={handleAcceptDocument}
                        disabled={isOffline}
                    />
                :
                <EqxTileBlank className='col-6' />}
        </EqxTileGroup>
    );
};

const PageControlledDocumentDetail = () => {
    const api = useApi()
    const { id } = useParams<{ id: string }>()

    const { response: document, loading, error, reset } = useApiEndpoint<ControlledDocumentModel>(
        api,
        api.endpoints.controlledDocument(id!)
    )

    return (
        <MainLayout
            pageTitle="Controlled Document Details"
            relatedLinks={[
                { url: '/controlled-documents', text: 'Back to Controlled Documents' }
            ]}
        >
            <EqxRequestHandler
                state={{ loading, error: error ?? null }}
                isEmpty={!document}
                empty={<div className="p-5 text-center text-muted">Document not found</div>}
                content={() => {
                    const doc = document!;
                    const isPdf = doc.url ? new URL(doc.url).pathname.toLowerCase().endsWith('.pdf') : false

                    const showHtml = doc.isHTML ?? false

                    return <>
                        <EqxCard
                            isSection
                            collapsible={false}
                            className="mb-3 no-padding"
                            header={<div className="d-flex">
                                {doc.businessFunction && (
                                    <div className="m-1 me-2">
                                        <BusinessFunctionBadge businessFunction={doc.businessFunction} />
                                    </div>
                                )}
                                <div>
                                    <h5 className="mb-0">{doc.title}</h5>
                                    <div className="text-muted">{doc.reference} <span className="badge bg-secondary">{doc.type?.name}</span></div>
                                </div>
                            </div>}
                            footer={doc.versionDate && (
                                <div className="text-muted small">
                                    <i className="fas fa-calendar-alt me-1"></i> Version Date: {formatDate(doc.versionDate)}
                                </div>
                            )}
                        >
                            {showHtml ? (
                                <div
                                    className="p-4 controlled-document-html"
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(doc.htmlContent) }}
                                />
                            ) : isPdf && doc.isInternal ? (
                                <PdfViewerPanel document={doc} />
                            ) : document?.url ? (
                                <div className="p-5 text-center text-muted">
                                    Preview not available for this document type<br />
                                    <EqxLink to={document!.url!} onClick={() => window.open(document!.url!, '_blank')}>Download</EqxLink>
                                </div>
                            ) : (
                                <div className="p-5 text-center text-danger"><strong>Error:</strong> File not available</div>
                            )}
                        </EqxCard>

                        <ControlledDocumentActions
                            document={doc}
                            documentId={id!}
                            onDocumentUpdated={reset}
                        />
                    </>
                }}
            >
            </EqxRequestHandler>
        </MainLayout>
    )
}

export default PageControlledDocumentDetail 